import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RecurringExpense {
  id: string;
  account_code: string | null;
  business_unit: 'us' | 'br' | null;
  name: string;
  description: string | null;
  vendor: string | null;
  amount: number;
  currency: string;
  cost_type: 'fixed' | 'variable';
  expense_type: 'fixed' | 'variable' | 'mixed';
  frequency: string;
  day_of_month: number;
  budget_amount: number | null;
  alert_threshold: number;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  estimated_min: number | null;
  estimated_max: number | null;
  variable_base: string | null;
  variable_rate: number | null;
  needs_invoice: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateRecurringExpenseData {
  name: string;
  business_unit: 'us' | 'br' | null;
  amount: number;
  currency?: string;
  cost_type: 'fixed' | 'variable';
  expense_type?: 'fixed' | 'variable' | 'mixed';
  account_code?: string;
  description?: string;
  vendor?: string;
  frequency?: string;
  day_of_month?: number;
  budget_amount?: number;
  alert_threshold?: number;
  start_date?: string;
  end_date?: string;
  estimated_min?: number;
  estimated_max?: number;
  variable_base?: string;
  variable_rate?: number;
  needs_invoice?: boolean;
}

export function useRecurringExpenses() {
  return useQuery({
    queryKey: ['recurring-expenses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recurring_expenses')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as RecurringExpense[];
    },
  });
}

export function useRecurringExpensesByBusinessUnit(businessUnit: 'us' | 'br' | 'all' = 'all') {
  return useQuery({
    queryKey: ['recurring-expenses', businessUnit],
    queryFn: async () => {
      let query = supabase
        .from('recurring_expenses')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (businessUnit !== 'all') {
        query = query.eq('business_unit', businessUnit);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as RecurringExpense[];
    },
  });
}

export function useCreateRecurringExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRecurringExpenseData) => {
      const { data: result, error } = await supabase
        .from('recurring_expenses')
        .insert({
          ...data,
          start_date: data.start_date || new Date().toISOString().split('T')[0],
        })
        .select()
        .single();

      if (error) throw error;
      return result as RecurringExpense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
    },
  });
}

export function useUpdateRecurringExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<RecurringExpense> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('recurring_expenses')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result as RecurringExpense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
    },
  });
}

export function useDeleteRecurringExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('recurring_expenses')
        .update({ is_active: false, end_date: new Date().toISOString().split('T')[0] })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
    },
  });
}

// Helper to calculate monthly equivalent based on frequency
function getMonthlyAmount(amount: number, frequency: string): number {
  switch (frequency) {
    case 'daily': return amount * 30;
    case 'weekly': return amount * 4.33;
    case 'monthly': return amount;
    case 'quarterly': return amount / 3;
    case 'semiannual': return amount / 6;
    case 'annual': return amount / 12;
    default: return amount;
  }
}

export function useRecurringExpensesSummary() {
  return useQuery({
    queryKey: ['recurring-expenses', 'summary'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recurring_expenses')
        .select('amount, currency, cost_type, budget_amount, business_unit, frequency')
        .eq('is_active', true);

      if (error) throw error;

      const expenses = data || [];
      
      const totalUSD = expenses
        .filter(e => e.currency === 'USD')
        .reduce((sum, e) => sum + getMonthlyAmount(e.amount || 0, e.frequency || 'monthly'), 0);
      
      const totalBRL = expenses
        .filter(e => e.currency === 'BRL')
        .reduce((sum, e) => sum + getMonthlyAmount(e.amount || 0, e.frequency || 'monthly'), 0);
      
      const fixed = expenses.filter(e => e.cost_type === 'fixed').length;
      const variable = expenses.filter(e => e.cost_type === 'variable').length;
      
      // Find expenses over budget
      const overBudget = expenses.filter(e => 
        e.budget_amount && e.amount > e.budget_amount
      );

      // Business unit breakdown
      const byBusinessUnit = {
        us: expenses.filter(e => e.business_unit === 'us').reduce((sum, e) => sum + (e.amount || 0), 0),
        br: expenses.filter(e => e.business_unit === 'br').reduce((sum, e) => sum + (e.amount || 0), 0),
        shared: expenses.filter(e => e.business_unit === null).reduce((sum, e) => sum + (e.amount || 0), 0),
      };
      
      return {
        count: expenses.length,
        totalUSD,
        totalBRL,
        costTypeBreakdown: { fixed, variable },
        fixedPercentage: expenses.length > 0 ? (fixed / expenses.length) * 100 : 0,
        overBudgetCount: overBudget.length,
        byBusinessUnit,
      };
    },
  });
}
